import ListingLayout from 'shared/components/listing-layout';
import { Table } from 'shared/components/table/table';
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface';
import { withErrorBoundary } from 'shared/hocs/with-error-boundary';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { usePagination } from 'shared/route-query/hooks/use-pagination';
import { getWorkflowEditorUrl, getWorkflowEditorUrlWithStep } from 'shared/utils/get-workflow-editor-url';
import { ContactId } from '../../types/contact-interface';
import { useContactWorkflows } from '../hooks/use-contact-workflows';
import { ContactWorkflowDelay, ContactWorkflowDelayTypeEnum, ContactWorkflowInterface } from '../types/contact-workflow-interface';
import { CheckCircleIcon, TimeIcon, OpenInNewIcon } from '@systemeio/ui/icons';
import { Hint } from '@systemeio/ui';
import { dateTimeFormat } from 'shared/utils/date-time-format';
import useUser from 'shared/hooks/use-user';
import { TIME_WITH_COUNT_LABELS } from 'shared/components/date-picker/time-delay-picker/constants';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
import { ContactWorkflowActionContent } from 'modules/contacts/contact-page/components/contact-workflow/contact-workflow-action-content';
import { ContactWorkflowAction } from 'modules/contacts/contact-page/types/contact-workflow-step-action-interface';
import { contactWorkflowActionTitleLocoKeys } from 'modules/contacts/contact-page/constants/contact-workflow-action-loco-keys';
import { getContactWorkflowStepActionIcon } from 'modules/contacts/contact-page/utils/get-contact-workflow-step-action-icon';
interface ContactWorkflowsProps {
  contactId: ContactId;
}
const ContactWorkflows = ({
  contactId
}: ContactWorkflowsProps) => {
  const {
    t,
    tICU
  } = useLocoTranslation();
  const {
    limit,
    pagination,
    setLimit,
    setPagination
  } = usePagination(true);
  const {
    user
  } = useUser();
  const {
    contactWorkflows
  } = useContactWorkflows({
    contactId,
    limit,
    pagination
  });
  const CompletedStep = ({
    taskCompletedAt
  }: {
    taskCompletedAt: string | undefined;
  }) => <div className="flex flex-col gap-1">
      <span className="flex items-center gap-2">
        <CheckCircleIcon className="fill-green" />
        <span className="text-text-primary">
          {t('dashboard.contact_page.workflows.finished_workflow')}
        </span>
      </span>
      <span className="text-[10px]">
        {taskCompletedAt && dateTimeFormat(taskCompletedAt, {
        dateFormat: user?.dateFormat,
        timeFormat: user?.timeFormat,
        timeZone: user?.timezone
      })}
      </span>
    </div>;
  const NotCompletedStep = ({
    action,
    delay,
    taskCreatedAt
  }: {
    action?: ContactWorkflowAction;
    delay?: ContactWorkflowDelay;
    taskCreatedAt: string;
  }) => <div>
      {delay && <DelayInfo delay={delay} />}
      {action && <ActionInfo action={action} />}
      <span className="text-[10px]">
        {dateTimeFormat(taskCreatedAt, {
        dateFormat: user?.dateFormat,
        timeFormat: user?.timeFormat,
        timeZone: user?.timezone
      })}
      </span>
    </div>;
  const DelayInfo = ({
    delay: delay_0
  }: {
    delay: ContactWorkflowDelay;
  }) => <div className="flex items-end gap-2">
      <TimeIcon className="fill-brand-primary" />
      <div className="flex gap-1">
        <span>{t('dashboard.contact_page.workflows.delay')}</span>
        <span>
          {delay_0.type === ContactWorkflowDelayTypeEnum.Date && dateTimeFormat(delay_0.date, {
          dateFormat: user?.dateFormat,
          timeFormat: user?.timeFormat,
          timeZone: user?.timezone
        })}
          {delay_0.type === ContactWorkflowDelayTypeEnum.Fixed && tICU(TIME_WITH_COUNT_LABELS[delay_0.unit], {
          count: delay_0.duration
        })}
        </span>
      </div>
    </div>;
  const ActionInfo = ({
    action: action_0
  }: {
    action: ContactWorkflowAction;
  }) => <div className="flex flex-col gap-1 whitespace-normal">
      <div className={'flex items-center gap-2'}>
        {getContactWorkflowStepActionIcon(action_0.type)}
        <span className="text-text-primary">
          {t(contactWorkflowActionTitleLocoKeys[action_0.type])}
        </span>
      </div>
      <span>
        <ContactWorkflowActionContent action={action_0} />
      </span>
    </div>;
  const columns: ColumnDefinitionInterface<ContactWorkflowInterface>[] = [{
    key: 'workflowStep',
    widthPercent: 50,
    header: t('dashboard.contact_page.workflows.headers.name'),
    onRender: ({
      workflowStep: {
        workflow
      }
    }) => <a href={getWorkflowEditorUrl(workflow.id)} rel="noreferrer" className="primary-link w-full whitespace-normal" target="__blank">
          {workflow.name}
        </a>
  }, {
    key: 'workflowStep',
    widthPercent: 50,
    header: t('dashboard.contact_page.workflows.headers.current_step'),
    onRender: ({
      workflowStep: {
        action: action_1,
        delay: delay_1,
        workflow: workflow_0,
        id: workflowStepId
      },
      isCompleted,
      taskCreatedAt: taskCreatedAt_0,
      taskCompletedAt: taskCompletedAt_0
    }) => <LinkWithoutPrefetch href={getWorkflowEditorUrlWithStep(workflow_0.id, workflowStepId)} className="flex justify-between">
          {isCompleted ? <CompletedStep taskCompletedAt={taskCompletedAt_0} /> : <NotCompletedStep action={action_1} delay={delay_1} taskCreatedAt={taskCreatedAt_0} />}
          <OpenInNewIcon className="fill-brand-primary" />
        </LinkWithoutPrefetch>
  }];
  return <ListingLayout counter={false} data={contactWorkflows} titleClassName={'truncate'} title={<Hint className={'truncate'} label={t('dashboard.contact_page.workflows.description')}>
          <h2 className={'h-[42px] truncate text-[17px] font-bold leading-[42px]'}>
            {t('header.top_menu.workflows')}
          </h2>
        </Hint>} type="compact">
      <Table columns={columns} data={contactWorkflows} paginator={{
      pagination,
      setPagination
    }} limiter={{
      limit,
      setLimit
    }} nestedId="workflowStep" />
    </ListingLayout>;
};
export default withErrorBoundary(ContactWorkflows);